import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
} from '@mui/material';
import useThemeStyles from '../../theme/useThemeStyles';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Compact photography & privacy settings for the current user on the Profile page.
 * Fetches partner data and shows only the logged-in user's preferences.
 */
const ProfilePhotographyPrivacy = () => {
  const styles = useThemeStyles();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    photoConsent: true,
    socialMediaConsent: false,
    anonymousInMarketing: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [partnerKey, setPartnerKey] = useState(null);
  const [allPartners, setAllPartners] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const response = await api.get('/api/wedding-details/partners');
        const { partnerOne, partnerTwo } = response.data || {};
        setAllPartners({ partnerOne, partnerTwo });

        const userId = user._id || user.id;
        const userEmail = user.email?.toLowerCase?.();

        let partner = null;
        let key = null;

        if (partnerOne?.userId?.toString?.() === userId?.toString?.() || partnerOne?.email?.toLowerCase?.() === userEmail) {
          partner = partnerOne;
          key = 'partnerOne';
        } else if (partnerTwo?.userId?.toString?.() === userId?.toString?.() || partnerTwo?.email?.toLowerCase?.() === userEmail) {
          partner = partnerTwo;
          key = 'partnerTwo';
        }

        if (partner && key) {
          setPartnerKey(key);
          setFormData({
            photoConsent: partner.photoConsent !== false,
            socialMediaConsent: partner.socialMediaConsent || false,
            anonymousInMarketing: partner.anonymousInMarketing || false,
          });
        }
      } catch {
        setPartnerKey(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSubmit = async () => {
    if (!partnerKey || !allPartners) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const partnersData = {
        partners: {
          partnerOne: allPartners.partnerOne ? {
            ...allPartners.partnerOne,
            ...(partnerKey === 'partnerOne' ? {
              photoConsent: formData.photoConsent,
              socialMediaConsent: formData.socialMediaConsent,
              anonymousInMarketing: formData.anonymousInMarketing,
            } : {}),
          } : {},
          partnerTwo: allPartners.partnerTwo ? {
            ...allPartners.partnerTwo,
            ...(partnerKey === 'partnerTwo' ? {
              photoConsent: formData.photoConsent,
              socialMediaConsent: formData.socialMediaConsent,
              anonymousInMarketing: formData.anonymousInMarketing,
            } : {}),
          } : {},
        },
      };

      await api.put('/api/wedding-details/partners', partnersData);
      setSuccess('Photography and privacy preferences saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to save photography and privacy preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card sx={styles.card.default}>
        <CardContent sx={styles.spacing.card}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!partnerKey) return null;

  return (
    <Card sx={styles.card.default}>
      <CardContent sx={styles.spacing.card}>
        <Typography variant="h6" sx={styles.text.sectionTitle}>
          Photography & Privacy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control how your images and details are used by suppliers
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.photoConsent}
                onChange={handleChange('photoConsent')}
              />
            }
            label="Consent to being photographed"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Allow photographers and videographers to capture your image during the wedding
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.socialMediaConsent}
                onChange={handleChange('socialMediaConsent')}
              />
            }
            label="Consent to suppliers sharing photos on social media"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Allow suppliers to use your wedding photos on their social media and marketing
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.anonymousInMarketing}
                onChange={handleChange('anonymousInMarketing')}
              />
            }
            label="Prefer to stay anonymous in press/marketing"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Request that your name and personal details are not used in press or marketing materials
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={18} /> : null}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfilePhotographyPrivacy;
